import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { formatErrorDetail } from "@/lib/format-error";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Plus, RefreshCw, Trash2, ArrowLeft, Save, Send, Upload, Download } from "lucide-react";

// Validate + normalise an imported JSON template payload. Returns
// {ok: true, value: {...}} or {ok: false, error: "..."}. Used by both
// the list-page "Import" button and the editor's in-place import.
const normaliseImportedTemplateJson = (raw) => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return { ok: false, error: "JSON must be an object with template fields." };
  }
  const html = typeof raw.html === "string" ? raw.html : "";
  const subject = typeof raw.subject === "string" ? raw.subject : "";
  if (!html.trim() || !subject.trim()) {
    return { ok: false, error: "JSON must include at least 'subject' and 'html' fields." };
  }
  return {
    ok: true,
    value: {
      name: typeof raw.name === "string" ? raw.name : "",
      subject,
      category: typeof raw.category === "string" ? raw.category : "tip",
      html,
      text: typeof raw.text === "string" ? raw.text : "",
      include_unsubscribe_footer:
        typeof raw.include_unsubscribe_footer === "boolean"
          ? raw.include_unsubscribe_footer
          : true,
    },
  };
};

const CATEGORIES = ["tip", "tutorial", "subscription", "other"];
const DEFAULT_SAMPLES = {
  // Native flat keys
  firstName: "Dean",
  lastName: "Loader",
  email: "dean@example.com",
  company: "Core Estimator",
  unsubscribeUrl: "http://localhost:3000/unsubscribe?token=preview",
  senderName: "Core Estimator",
  currentYear: String(new Date().getFullYear()),
  // HubSpot-style dotted aliases — preview matches what production substitution does
  "contact.firstname": "Dean",
  "contact.lastname": "Loader",
  "contact.email": "dean@example.com",
  "contact.company": "Core Estimator",
  "contact.first_name": "Dean",
  "contact.last_name": "Loader",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const CategoryBadge = ({ category }) => {
  const styles = {
    tip: "bg-indigo-100 text-indigo-700",
    tutorial: "bg-blue-100 text-blue-700",
    subscription: "bg-emerald-100 text-emerald-700",
    other: "bg-slate-100 text-slate-700",
  };
  return <Badge className={`${styles[category] || styles.other} hover:${styles[category] || styles.other}`}>{category}</Badge>;
};

// ----- Editor view -----
const TemplateEditor = ({ template, onCancel, onSaved, authHeaders, currentUserEmail, orgTestRecipient }) => {
  const isNew = !template?.id;
  const [name, setName] = useState(template?.name || "");
  const [subject, setSubject] = useState(template?.subject || "");
  const [category, setCategory] = useState(template?.category || "tip");
  const [html, setHtml] = useState(template?.html || "");
  const [text, setText] = useState(template?.text || "");
  const [includeUnsubscribeFooter, setIncludeUnsubscribeFooter] = useState(
    template?.include_unsubscribe_footer ?? true,
  );
  const [testRecipient, setTestRecipient] = useState(orgTestRecipient || currentUserEmail || "");
  const [sendingTest, setSendingTest] = useState(false);
  const [sampleVars, setSampleVars] = useState({ ...DEFAULT_SAMPLES });
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [detectedVars, setDetectedVars] = useState([]);
  const [missingVars, setMissingVars] = useState([]);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const editorImportInputRef = useRef(null);

  // Replace all editor fields with the contents of an imported JSON template.
  // Overwrites whatever's currently in the editor — user clicked Import
  // deliberately, no confirmation prompt needed.
  const handleEditorImport = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let raw;
      try {
        raw = JSON.parse(reader.result);
      } catch {
        toast.error("That file isn't valid JSON.");
        return;
      }
      const result = normaliseImportedTemplateJson(raw);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      const v = result.value;
      setName(v.name);
      setSubject(v.subject);
      setCategory(v.category);
      setHtml(v.html);
      setText(v.text);
      setIncludeUnsubscribeFooter(v.include_unsubscribe_footer);
      toast.success("Imported. Review the fields and Save when ready.");
    };
    reader.onerror = () => toast.error("Failed to read the file.");
    reader.readAsText(file);
  };

  // Build a JSON payload from current editor state and trigger a download.
  // Includes every field the import flow accepts so the round-trip is lossless.
  const handleExportJson = () => {
    const payload = {
      name: name.trim(),
      subject: subject.trim(),
      category,
      html,
      text: text || "",
      include_unsubscribe_footer: includeUnsubscribeFooter,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeName = (name || "template").replace(/[^A-Za-z0-9_-]+/g, "_");
    a.download = `${safeName}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Live preview — debounced server-side render
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API}/email-templates/preview`,
          { html, text, subject, sample_vars: sampleVars },
          { withCredentials: true, headers: authHeaders },
        );
        setPreviewHtml(res.data.html || "");
        setPreviewSubject(res.data.subject || "");
        setDetectedVars(res.data.detected_vars || []);
        setMissingVars(res.data.missing_vars || []);
      } catch (err) {
        // Silent — preview is best-effort. Save will surface real errors.
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [html, subject, text, sampleVars, authHeaders]);

  const updateSampleVar = (key, value) => {
    setSampleVars((s) => ({ ...s, [key]: value }));
  };

  // For each detected var, ensure it has a sample-data input
  const sampleInputs = useMemo(() => {
    const all = new Set([...detectedVars, ...Object.keys(DEFAULT_SAMPLES)]);
    return Array.from(all);
  }, [detectedVars]);

  const handleTestSend = async () => {
    if (!subject.trim() || !html.trim()) {
      toast.error("Subject and HTML are required to send a test");
      return;
    }
    if (!testRecipient.trim()) {
      toast.error("Enter an email address to send the test to");
      return;
    }
    setSendingTest(true);
    try {
      const res = await axios.post(
        `${API}/email-templates/test-send`,
        {
          html,
          subject: subject.trim(),
          text: text || null,
          include_unsubscribe_footer: includeUnsubscribeFooter,
          recipient_email: testRecipient.trim(),
          recipient_name: "Test recipient",
          sample_vars: sampleVars,
          template_name: name.trim() || null,
        },
        { withCredentials: true, headers: authHeaders },
      );
      if (res.data.success) {
        const extra = res.data.missing_vars?.length ? ` (missing vars: ${res.data.missing_vars.join(", ")})` : "";
        toast.success(`Test sent to ${testRecipient.trim()}${extra}`);
      } else {
        toast.error(res.data.error || "Test send failed");
      }
    } catch (err) {
      toast.error(formatErrorDetail(err, "Test send failed"));
    } finally {
      setSendingTest(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim() || !subject.trim() || !html.trim()) {
      toast.error("Name, subject, and HTML are all required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        subject: subject.trim(),
        html,
        text: text || null,
        category,
        include_unsubscribe_footer: includeUnsubscribeFooter,
      };
      const res = isNew
        ? await axios.post(`${API}/email-templates`, body, { withCredentials: true, headers: authHeaders })
        : await axios.put(`${API}/email-templates/${template.id}`, body, { withCredentials: true, headers: authHeaders });
      toast.success(isNew ? "Template created" : "Template updated");
      onSaved(res.data);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h2 className="text-xl font-semibold text-slate-900">
            {isNew ? "New template" : `Edit: ${template?.name}`}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept="application/json,.json"
            ref={editorImportInputRef}
            onChange={handleEditorImport}
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => editorImportInputRef.current?.click()}
            title="Replace all fields with the contents of a JSON file"
          >
            <Upload className="w-4 h-4 mr-1" /> Import JSON
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportJson}
            title="Download the current template as JSON"
          >
            <Download className="w-4 h-4 mr-1" /> Export JSON
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor pane */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Editor</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="tpl-name">Name (internal)</Label>
                <Input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tip 01 — Welcome" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tpl-cat">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger id="tpl-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tpl-subject">Subject (recipient sees this)</Label>
              <Input
                id="tpl-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Welcome to the builder series, {{firstName}}"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tpl-html">HTML body</Label>
              <Textarea
                id="tpl-html"
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                placeholder="<html>...{{firstName}}...{{unsubscribeUrl}}...</html>"
                className="font-mono text-xs min-h-[260px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tpl-text" className="flex items-center justify-between">
                <span>Plain-text fallback (optional)</span>
                <span className="text-xs font-normal text-slate-500">leave blank to auto-strip from HTML</span>
              </Label>
              <Textarea
                id="tpl-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Hi {{firstName}}, ..."
                className="font-mono text-xs min-h-[100px]"
              />
            </div>
            <div className="flex items-start gap-3 p-3 rounded-md border border-slate-200 bg-slate-50">
              <input
                id="tpl-unsub-footer"
                type="checkbox"
                checked={includeUnsubscribeFooter}
                onChange={(e) => setIncludeUnsubscribeFooter(e.target.checked)}
                className="mt-1 w-4 h-4"
              />
              <div className="flex-1">
                <Label htmlFor="tpl-unsub-footer" className="text-sm font-medium cursor-pointer">
                  Add unsubscribe footer to this template
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  Recommended for marketing emails. Skip for transactional emails (receipts, password resets, etc.).
                  If you've already added <code className="text-xs bg-slate-100 px-1 rounded">{"{{unsubscribeUrl}}"}</code> in your HTML, the footer is skipped automatically so it doesn't double up.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
              </Button>
              <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
            </div>

            <div className="pt-4 border-t border-slate-200 space-y-2">
              <Label htmlFor="tpl-test-recipient" className="text-sm font-medium">
                Send a test
              </Label>
              <p className="text-xs text-slate-500">
                Sends the current draft (no save needed) so you can see exactly how it'll render in your inbox.
              </p>
              <div className="flex gap-2">
                <Input
                  id="tpl-test-recipient"
                  type="email"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  placeholder="you@example.com"
                  disabled={sendingTest}
                  className="flex-1"
                />
                <Button
                  onClick={handleTestSend}
                  disabled={sendingTest || !html.trim() || !subject.trim() || !testRecipient.trim()}
                  variant="secondary"
                >
                  {sendingTest ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send test</>}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preview pane */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Preview</CardTitle>
            <CardDescription>
              Detected variables: {detectedVars.length === 0 ? <em>none</em> : detectedVars.map((v) => (
                <Badge key={v} variant="outline" className="ml-1 text-xs">{v}</Badge>
              ))}
              {missingVars.length > 0 && (
                <div className="text-amber-600 text-xs mt-2">
                  Missing sample data for: {missingVars.join(", ")} (will render as empty when sent)
                </div>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Sample data (preview only — not saved)</Label>
              <div className="grid grid-cols-2 gap-2">
                {sampleInputs.map((key) => (
                  <div key={key} className="space-y-1">
                    <Label htmlFor={`sample-${key}`} className="text-xs font-mono">{`{{${key}}}`}</Label>
                    <Input
                      id={`sample-${key}`}
                      value={sampleVars[key] ?? ""}
                      onChange={(e) => updateSampleVar(key, e.target.value)}
                      className="text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Subject preview</Label>
              <div className="text-sm font-medium border border-slate-200 rounded px-3 py-2 bg-slate-50">
                {previewSubject || <em className="text-slate-400">empty</em>}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">HTML preview</Label>
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={previewHtml}
                className="w-full min-h-[400px] border border-slate-200 rounded bg-white"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ----- List view -----
const EmailTemplates = ({ user, setUser }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null); // null = list view; {id?...} = editor
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [orgTestRecipient, setOrgTestRecipient] = useState("");
  const listImportInputRef = useRef(null);

  // Triggered when the user picks a JSON file from the list-page Import
  // button. Validates + opens the editor pre-filled with the imported
  // values (same shape the editor uses for new / duplicated templates).
  const handleListPageImport = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so the same file can be re-selected later
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let raw;
      try {
        raw = JSON.parse(reader.result);
      } catch {
        toast.error("That file isn't valid JSON.");
        return;
      }
      const result = normaliseImportedTemplateJson(raw);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      setEditingTemplate(result.value);
      toast.success("Imported. Review the fields and click Save to create the template.");
    };
    reader.onerror = () => toast.error("Failed to read the file.");
    reader.readAsText(file);
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-templates`, { withCredentials: true, headers: authHeaders });
      setTemplates(res.data.templates || []);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to load templates"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // Pull org-level test-send recipient setting once so the editor can pre-fill it.
    (async () => {
      try {
        const res = await axios.get(`${API}/settings`, { withCredentials: true, headers: authHeaders });
        setOrgTestRecipient(res.data?.test_send_recipient_email || "");
      } catch (err) { /* silent — editor falls back to user email */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleDelete = async (template) => {
    try {
      await axios.delete(`${API}/email-templates/${template.id}`, { withCredentials: true, headers: authHeaders });
      toast.success("Template deleted");
      setConfirmDelete(null);
      fetchTemplates();
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to delete"));
    }
  };

  const handleSaved = () => {
    setEditingTemplate(null);
    fetchTemplates();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="email-templates-page">
      <Sidebar user={user} onLogout={handleLogout} />
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Email Templates
            </h1>
            <p className="text-slate-500 mt-1">
              Paste compiled HTML, define <code className="text-xs bg-slate-100 px-1 rounded">{"{{varname}}"}</code> placeholders, send from the Email Tools page.
            </p>
          </div>
          {!editingTemplate && (
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="application/json,.json"
                ref={listImportInputRef}
                onChange={handleListPageImport}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => listImportInputRef.current?.click()}
                title="Import a template from a JSON file"
              >
                <Upload className="w-4 h-4 mr-2" /> Import JSON
              </Button>
              <Button onClick={() => setEditingTemplate({})} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" /> New Template
              </Button>
            </div>
          )}
        </div>

        {editingTemplate ? (
          <TemplateEditor
            template={editingTemplate}
            onCancel={() => setEditingTemplate(null)}
            onSaved={handleSaved}
            authHeaders={authHeaders}
            currentUserEmail={user?.email || ""}
            orgTestRecipient={orgTestRecipient}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" /> Library</span>
                <Button variant="ghost" size="sm" onClick={fetchTemplates} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
              </CardTitle>
              <CardDescription>{templates.length} template{templates.length === 1 ? "" : "s"}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-slate-500 text-sm">Loading…</p>
              ) : templates.length === 0 ? (
                <p className="text-slate-500 text-sm">No templates yet — click "New Template" to create one.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Variables</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((t) => (
                      <TableRow key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setEditingTemplate(t)}>
                        <TableCell className="font-medium">{t.name}</TableCell>
                        <TableCell><CategoryBadge category={t.category} /></TableCell>
                        <TableCell className="text-slate-600 text-sm max-w-xs truncate" title={t.subject}>{t.subject}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(t.variables || []).slice(0, 4).map((v) => (
                              <Badge key={v} variant="outline" className="text-xs font-mono">{v}</Badge>
                            ))}
                            {(t.variables || []).length > 4 && (
                              <Badge variant="outline" className="text-xs">+{t.variables.length - 4}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500 text-xs">{formatDate(t.updated_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setConfirmDelete(t); }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.name}" will be permanently deleted. Past sends that used this template are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(confirmDelete)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmailTemplates;
